import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { RootState } from '@/store/root-reducer';

import type { DashboardScope } from '../domain/scope';
import type { DashboardGateway } from '../infrastructure/dashboard.gateway';
import { dashboardRequested, scopeRequested } from './dashboard.actions';
import { dashboardActions } from './dashboard.slice';

const LOAD_ERROR = 'Failed to load dashboard data.';

/** Best-effort human message for a panel's error state (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER for one panel: call its gateway read, then dispatch its `*Loaded` or
 * `*Failed` transition. Each panel try/catches on its own so a failing endpoint
 * marks only its card (the legacy page's per-request `.catch` resilience).
 */
function* loadPanel<T>(
  read: () => Promise<T>,
  loaded: ActionCreatorWithPayload<T>,
  failed: ActionCreatorWithPayload<string>,
) {
  try {
    const data = (yield call(read)) as T;
    yield put(loaded(data));
  } catch (e) {
    yield put(failed(toMessage(e, LOAD_ERROR)));
  }
}

/** The scoped reads, under `scope`; the panels a click narrows. */
function scopedReads(gateway: DashboardGateway, scope: DashboardScope) {
  const a = dashboardActions;
  return [
    loadPanel(() => gateway.fetchStats(scope), a.statsLoaded, a.statsFailed),
    loadPanel(() => gateway.fetchSourceSplit(scope), a.sourceSplitLoaded, a.sourceSplitFailed),
    loadPanel(() => gateway.fetchTypeCounts(scope), a.categoryCountsLoaded, a.categoryCountsFailed),
    loadPanel(() => gateway.fetchCountryDistribution(scope), a.countriesLoaded, a.countriesFailed),
    loadPanel(() => gateway.fetchMonthlyAdded(scope), a.monthlyAddedLoaded, a.monthlyAddedFailed),
  ];
}

/**
 * WORKER that loads the whole board: mark every panel loading, then run the
 * reads concurrently, under the current scope. `takeLatest` cancels the
 * in-flight batch when a newer request arrives.
 */
function* loadDashboardWorker() {
  const gateway: DashboardGateway = yield getContext('dashboardGateway');
  const scope: DashboardScope = yield select((s: RootState) => s.dashboard.scope);
  const a = dashboardActions;

  yield put(a.loadStarted());
  // Iterators passed straight to `all` run concurrently, like forked calls;
  // and keep each panel's generic types intact. Notifications, lists,
  // top-compatible and the latest fundraising are not read: what they feed is
  // hidden (#637), see LOADED_PANELS.
  yield all([
    loadPanel(gateway.fetchLatestDelivery, a.latestDeliveryLoaded, a.latestDeliveryFailed),
    loadPanel(gateway.fetchTrackedProfiles, a.trackedLpsLoaded, a.trackedLpsFailed),
    ...scopedReads(gateway, scope),
  ]);
}

/**
 * WORKER for a new scope: re-read only the panels it narrows. No loading state:
 * the page keeps its figures (dimmed by `refreshing`) until the new ones land,
 * so a click never blanks the board. `takeLatest` drops a scope clicked past.
 */
function* scopeWorker(action: ReturnType<typeof scopeRequested>) {
  const gateway: DashboardGateway = yield getContext('dashboardGateway');
  const a = dashboardActions;

  yield put(a.scopeSet(action.payload));
  try {
    yield all(scopedReads(gateway, action.payload));
  } finally {
    yield put(a.refreshDone());
  }
}

/** WATCHERS. */
export function* dashboardSaga() {
  yield all([
    takeLatest(dashboardRequested.type, loadDashboardWorker),
    takeLatest(scopeRequested.type, scopeWorker),
  ]);
}
